import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companyApi, userCompanyApi, ListCompaniesParams } from '../services/userApi';
import { CompanyCreate, CompanyUpdate, CompanyRole } from '../types';

export const COMPANIES_QUERY_KEY = 'companies';
export const MY_COMPANIES_QUERY_KEY = 'my-companies';
export const USER_COMPANIES_QUERY_KEY = 'user-companies';

// Backwards-compatible alias retained for now; prefer COMPANIES_QUERY_KEY.
export const BUSINESSES_QUERY_KEY = COMPANIES_QUERY_KEY;

export function useCompanies(params?: ListCompaniesParams) {
  return useQuery({
    queryKey: [COMPANIES_QUERY_KEY, params],
    queryFn: () => companyApi.list(params),
  });
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: [COMPANIES_QUERY_KEY, id],
    queryFn: () => companyApi.get(id),
    enabled: !!id,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CompanyCreate) => companyApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_COMPANIES_QUERY_KEY] });
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CompanyUpdate }) =>
      companyApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_COMPANIES_QUERY_KEY] });
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => companyApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_COMPANIES_QUERY_KEY] });
    },
  });
}

/**
 * Companies the current user can access (multi-company tenants).
 * Single-company tenants always have exactly one entry — pickers/filters
 * auto-hide when `data.length === 1`.
 */
export function useMyCompanies() {
  return useQuery({
    queryKey: [MY_COMPANIES_QUERY_KEY],
    queryFn: () => companyApi.listMine(),
    staleTime: 60_000,
  });
}

// ============== Per-user company access (admin) ==============

/** A user's per-company access grants (admin company-access editor). */
export function useUserCompanies(userId: string) {
  return useQuery({
    queryKey: [USER_COMPANIES_QUERY_KEY, userId],
    queryFn: () => userCompanyApi.list(userId),
    enabled: !!userId,
  });
}

export function useAddUserCompany(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { company_id: string; role?: CompanyRole }) =>
      userCompanyApi.add(userId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [USER_COMPANIES_QUERY_KEY, userId] }),
  });
}

export function useUpdateUserCompanyRole(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, role }: { companyId: string; role: CompanyRole }) =>
      userCompanyApi.updateRole(userId, companyId, role),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [USER_COMPANIES_QUERY_KEY, userId] }),
  });
}

export function useRemoveUserCompany(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => userCompanyApi.remove(userId, companyId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [USER_COMPANIES_QUERY_KEY, userId] }),
  });
}
